import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { HeartbeatPreview } from "../types";

type Props = {
  preview?: HeartbeatPreview;
  /** what to say when there is no frame yet: "waiting for preview frame" */
  placeholder: string;
};

/**
 * Always rendered, so the panel keeps its shape instead of collapsing when frames
 * stop arriving. The box stretches to the height of the settings column, so the
 * frame is contained rather than cropped: a preview whose aspect ratio differs from
 * the box letterboxes instead of distorting.
 *
 * A frame the browser cannot decode is reported rather than left blank. Nothing is
 * thrown for a bad `data:` URI — the <img> simply draws nothing, which is
 * indistinguishable from a working preview of a dark scene — so the one signal the
 * browser does give, onError, is turned into the empty state. The failing src is
 * remembered rather than a bare flag, so the next frame gets its own attempt
 * instead of inheriting the last one's verdict.
 */
export function PreviewPane({ preview, placeholder }: Props) {
  const { t } = useTranslation();
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const src = preview ? `data:${preview.mime};base64,${preview.b64}` : null;
  const broken = !!src && src === failedSrc;
  const showImage = !!src && !broken;

  return (
    <div
      className={`relative box-border w-full self-stretch h-auto rounded-[10px] overflow-hidden border flex items-center justify-center max-[640px]:aspect-[16/9] ${
        showImage
          ? "border-white/[0.16] bg-black"
          : "border-dashed border-white/[0.16] bg-white/[0.03]"
      }`}
    >
      {showImage ? (
        <img
          className="w-full h-full object-contain block"
          alt={t("HeadsetDock.PreviewAlt")}
          src={src}
          onError={() => setFailedSrc(src)}
        />
      ) : (
        // the one place the design names a font other than Montserrat
        <span className="font-['Courier_New',monospace] text-[11px] text-white/60 tracking-[0.06em]">
          {broken ? t("HeadsetDock.PreviewUnreadable") : placeholder}
        </span>
      )}
    </div>
  );
}
